// §4.2 Stock Unit Card (office) — "everything about bobbin 1645 on one screen".
//
// Header: serial + kind + status pill · product + lot (tap → §4.6 lot detail) ·
// the remaining/capacity DONUT with the exact numeric · the claimed-vs-measured
// DRIFT badge (fires past |Δ| > 2%, §4.2) · the location breadcrumb · the
// role-gated action bar (Cut, Re-measure, Move, Reserve, Quarantine/Release,
// Retire) · the three tabs (History / Genealogy / Reservations).
//
// Drift: inventory truth is ALWAYS the measured value (§9.4) — the badge points
// at Module 04's calibration story, and while 04 is unbound it says so instead
// of linking nowhere.
//
// The action bar mirrors §7 with the fail-closed hook; the server is the
// authority. `#cut` in the URL opens the cut panel directly, which is how the
// §4.1 result card's "Cut" arrives here.

import { enumLabel } from "../../../../lib/enum-label";
import { shortId } from "../../../../lib/short-id";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { CardSkeleton, ErrorState } from "../../../../components/states";
import { NavTabs } from "../../../../components/tabs";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useTerm } from "../../../onboarding/hooks/terminology";
import { Field, InventoryStatus, LocationPath, RemainingDonut } from "../components/inventory-ui";
import { ReservePanel, type ReserveTarget } from "../components/reserve-panel";
import {
  useLot,
  useProductIndex,
  useReleaseReservation,
  useStockUnit,
} from "../hooks/inventory-queries";
import { useInventoryPermissions } from "../hooks/permissions";
import { useProductionPermissions } from "../../production/hooks/permissions";
import { inventoryErrorMessage } from "../lib/errors";
import {
  driftExceedsThreshold,
  driftPct,
  formatMeasure,
  formatPct,
  formatTimestamp,
} from "../lib/measure";
import {
  blockedReasonText,
  unitActionBlock,
  type UnitBlockReason,
  type UnitCardAction,
} from "../lib/unit-actions";
import { UnitActionPanel, type UnitAction } from "./unit-card/action-panels";
import { GenealogyTab, HistoryTab, ReservationsTab } from "./unit-card/unit-tabs";

type TabKey = "history" | "genealogy" | "reservations";

const ACTIONS_FROM_HASH: Readonly<Record<string, UnitAction>> = {
  "#cut": "cut",
  "#remeasure": "remeasure",
  "#move": "move",
};

export function StockUnitCardScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { term } = useTerm();
  const perms = useInventoryPermissions();
  const productionPerms = useProductionPermissions();
  const params = useParams({ strict: false });
  const id = typeof params.id === "string" ? params.id : "";

  const card = useStockUnit(id);
  const [tab, setTab] = useState<TabKey>("history");
  const [action, setAction] = useState<UnitAction | null>(
    () => ACTIONS_FROM_HASH[window.location.hash] ?? null,
  );
  const [reserveTarget, setReserveTarget] = useState<ReserveTarget | null>(null);

  const productIds =
    card.data?.unit.product_id !== undefined && card.data.unit.product_id !== null
      ? [card.data.unit.product_id]
      : [];
  const products = useProductIndex(productIds);

  // §4.2 calls this "everything about bobbin 1645 on one screen", and the first
  // question is "can I use it now": status + QC + hold in one glance. The unit
  // payload carries no lot qc_status, so the lot is fetched for its code and
  // pill — a `filled` unit whose lot is still `pending` QC must not read as
  // usable (the §4.1 card badges it and this one did not).
  const lot = useLot(card.data?.unit.lot_id ?? "");

  const releaseHold = useReleaseReservation();
  const [confirmRelease, setConfirmRelease] = useState(false);

  useEffect(() => {
    if (card.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [card.error, navigate]);

  // TanStack Router keeps the SAME component instance across a param change
  // unless `remountDeps` is set, so navigating 1645 → 1645-1 (the Genealogy
  // tab's tap-through) would otherwise leave a Cut panel open holding the
  // PREVIOUS unit's piece lengths, staged against the new one. Every panel is
  // closed on the id, and the panels are keyed so their internal state cannot
  // survive either.
  useEffect(() => {
    setAction(null);
    setReserveTarget(null);
    setConfirmRelease(false);
    setTab("history");
  }, [id]);

  if (card.isPending || card.error instanceof UnauthorizedError) {
    return <CardSkeleton lines={6} label={t("inventory.unitCard.loading")} />;
  }

  if (card.error) {
    const notFound = card.error instanceof RequestError && card.error.httpStatus === 404;
    return (
      <ErrorState
        title={
          notFound ? t("inventory.unitCard.notFoundTitle") : t("inventory.unitCard.errorTitle")
        }
        detail={notFound ? t("inventory.unitCard.notFoundBody") : card.error.message}
        retryLabel={t("inventory.common.retry")}
        onRetry={() => void card.refetch()}
        requestId={requestIdOf(card.error)}
        requestIdLabel={t("inventory.common.requestId")}
      />
    );
  }

  const { unit, active_reservation: activeReservation, last_scans: lastScans } = card.data;
  const product = unit.product_id !== null ? products.byId.get(unit.product_id) : undefined;
  const drift = driftPct(unit.claimed_measure, unit.initial_measure);
  const driftBadge = driftExceedsThreshold(drift);

  const canCut = perms.has("inventory.unit.cut");
  const canScan = perms.has("inventory.scan.locate");
  const canAdjust = perms.has("inventory.adjust");
  const canReserve = perms.has("inventory.reservation.manage");
  const canQuarantine = perms.has("inventory.quarantine");
  const canTrace = perms.has("inventory.trace.read");

  const lastScan = lastScans[0];

  return (
    <section
      className="unit-card"
      data-testid="unit-card"
      data-serial={unit.serial_code}
      data-status={unit.status}
      data-remaining={unit.remaining_measure ?? ""}
      data-claimed={unit.claimed_measure ?? ""}
      data-initial={unit.initial_measure ?? ""}
      data-location={unit.location_path ?? ""}
      data-offcut={String(unit.is_offcut)}
    >
      <PageHeader
        crumbs={[{ label: t("inventory.lookup.title"), to: "/inventory/lookup" }]}
        title={
          <>
            <span className="unit-serial">{unit.serial_code}</span>{" "}
            <span className="unit-kind">{enumLabel(t, "inventory.vocab.unitKind", unit.kind)}</span>
          </>
        }
        actions={<InventoryStatus status={unit.status} scope="status" />}
      />

      <div className="unit-summary">
        <RemainingDonut
          remaining={unit.remaining_measure}
          capacity={unit.capacity_measure}
          uom={unit.measure_uom}
        />

        <dl className="inv-fields">
          <Field label={t("inventory.unitCard.product")}>
            {unit.product_id === null ? (
              t("inventory.unitCard.bareContainer")
            ) : (
              <Link to="/catalogue/products/$id" params={{ id: unit.product_id }}>
                {product === undefined ? unit.product_id : `${product.sku} · ${product.name}`}
              </Link>
            )}
          </Field>
          <Field label={term("lot")}>
            {unit.lot_id === null ? (
              t("inventory.common.none")
            ) : (
              <>
                <Link to="/inventory/lots/$id" params={{ id: unit.lot_id }}>
                  {lot.data?.lot.lot_code ?? t("inventory.unitCard.openLot")}
                </Link>{" "}
                {lot.data !== undefined ? (
                  <InventoryStatus status={lot.data.lot.qc_status} scope="qc" />
                ) : null}
              </>
            )}
          </Field>
          <Field label={term("location")}>
            <LocationPath path={unit.location_path} />
          </Field>
          <Field label={t("inventory.unitCard.claimedVsMeasured")}>
            {unit.claimed_measure === null || unit.initial_measure === null ? (
              <span className="inv-muted">{t("inventory.unitCard.noClaim")}</span>
            ) : (
              <>
                <span className="drift-values">
                  {t("inventory.unitCard.claimedMeasured", {
                    claimed: formatMeasure(unit.claimed_measure, { locale: i18n.language }),
                    measured: formatMeasure(unit.initial_measure, { locale: i18n.language }),
                    uom: unit.measure_uom ?? "",
                  })}
                </span>{" "}
                {driftBadge && drift !== undefined ? (
                  <span className="status-badge status-warning" data-testid="drift-badge">
                    {t("inventory.unitCard.driftBadge", { pct: formatPct(drift) })}
                  </span>
                ) : null}{" "}
                {/* §4.2 links the drift badge to Module 04's machine drift
                    chart, for the roles that can read it. The row that said
                    "Work-order figures not counted" in its place was false
                    once production went live, and is gone (D-425). */}
                {driftBadge && productionPerms.has("production.telemetry.read") ? (
                  <Link
                    to="/production/calibration"
                    className="ds-lnk"
                    data-testid="drift-calibration"
                  >
                    {t("inventory.unitCard.driftOwner")}
                  </Link>
                ) : null}
              </>
            )}
          </Field>
          <Field label={t("inventory.unitCard.lastScan")}>
            {lastScan === undefined ? (
              <span className="inv-muted">{t("inventory.lookup.neverScanned")}</span>
            ) : (
              t("inventory.unitCard.lastScanValue", {
                when: formatTimestamp(lastScan.occurred_at, i18n.language),
                action: enumLabel(t, "inventory.vocab.scan", lastScan.action),
              })
            )}
          </Field>
          {activeReservation !== null ? (
            <Field label={t("inventory.unitCard.activeHold")}>
              {t("inventory.unitCard.heldFor", {
                demand: enumLabel(t, "inventory.reserve.kind", activeReservation.demand_type),
                quantity: formatMeasure(activeReservation.quantity, { locale: i18n.language }),
                uom: activeReservation.uom,
              })}{" "}
              {/* The WORDS the hold was placed under where it has them
                  (D-151): "held for BuildCo · Thursday" is what a storekeeper
                  standing at the drum can act on. The short id is the fallback
                  for the module-minted kinds, which carry no reference. */}
              {activeReservation.demand_ref !== null ? (
                <span className="demand-ref">{activeReservation.demand_ref}</span>
              ) : (
                <code className="ref-id">{shortId(activeReservation.demand_id)}</code>
              )}
              {/* A hold that cannot be released keeps the stock out of ATP and
                  out of allocation until a sweeper expires it — §5 row 30 is
                  the release, and this is its only office caller. */}
              {canReserve && activeReservation.status === "active" ? (
                releaseHold.isSuccess ? (
                  <span className="inv-success">{t("inventory.unitCard.holdReleased")}</span>
                ) : confirmRelease ? (
                  <span className="inline-confirm">
                    <span className="inv-warning">
                      {t("inventory.unitCard.releaseHoldConfirm", {
                        demand: enumLabel(
                          t,
                          "inventory.reserve.kind",
                          activeReservation.demand_type,
                        ),
                        id: activeReservation.demand_ref ?? shortId(activeReservation.demand_id),
                      })}
                    </span>
                    <Button
                      variant="primary"
                      busy={releaseHold.isPending}
                      onClick={() => {
                        releaseHold.mutate({ id: activeReservation.id });
                        setConfirmRelease(false);
                      }}
                    >
                      {t("inventory.common.confirm")}
                    </Button>
                    <Button
                      onClick={() => {
                        setConfirmRelease(false);
                      }}
                    >
                      {t("inventory.common.cancel")}
                    </Button>
                  </span>
                ) : (
                  <Button
                    onClick={() => {
                      setConfirmRelease(true);
                    }}
                  >
                    {t("inventory.unitCard.releaseHold")}
                  </Button>
                )
              ) : null}
              {releaseHold.isError ? (
                <span className="form-error" role="alert">
                  {inventoryErrorMessage(
                    t,
                    releaseHold.error,
                    "inventory.unitCard.releaseHoldFailed",
                  )}
                </span>
              ) : null}
            </Field>
          ) : null}
        </dl>
      </div>

      <UnitActionBar
        status={unit.status}
        reasonId={`unit-actions-reason-${unit.id}`}
        actions={[
          ...(canCut ? [{ key: "cut" as const, testId: "unit-action-cut" }] : []),
          ...(canScan ? [{ key: "remeasure" as const }] : []),
          ...(canAdjust ? [{ key: "move" as const }] : []),
          ...(canReserve && unit.product_id !== null && unit.remaining_measure !== null
            ? [{ key: "reserve" as const }]
            : []),
          ...(canQuarantine
            ? [
                {
                  key:
                    unit.status === "quarantined" ? ("release" as const) : ("quarantine" as const),
                },
              ]
            : []),
          ...(canAdjust ? [{ key: "retire" as const }] : []),
        ]}
        onAction={(key) => {
          if (key === "reserve") {
            setReserveTarget({
              productId: unit.product_id ?? "",
              facilityId: unit.facility_id,
              uom: unit.measure_uom ?? "pcs",
              quantity: unit.remaining_measure ?? "0",
              stockUnitId: unit.id,
              ...(unit.lot_id !== null ? { lotId: unit.lot_id } : {}),
              label: unit.serial_code,
            });
            return;
          }
          setAction(key);
        }}
      />

      {action !== null ? (
        <UnitActionPanel
          key={`${unit.id}-${action}`}
          action={action}
          unit={unit}
          onClose={() => {
            setAction(null);
          }}
        />
      ) : null}

      {reserveTarget !== null ? (
        <ReservePanel
          key={unit.id}
          target={reserveTarget}
          onClose={() => {
            setReserveTarget(null);
          }}
        />
      ) : null}

      {/* §3.10's `NavTabs`: a <nav> of buttons with `aria-current`, for a strip
          that switches panels WITHOUT a URL — which is exactly what this one
          does. It was hand-rolled (`.detail-tabs` / `.detail-tab`, classes no
          stylesheet in this area ever defined, so the strip rendered as three
          bare buttons), and "one implementation of tab semantics, ever" is the
          register's rule, not a preference. The `unit-tab-*` testids the M06
          walkthroughs drive are unchanged; the active marker is now the
          primitive's `aria-current="page"`, the same value the catalogue's
          product-detail strip has carried since UX-2. */}
      <NavTabs
        className="detail-tabs"
        label={t("inventory.unitCard.tabsAria")}
        active={tab}
        onSelect={(next) => {
          setTab(next as typeof tab);
        }}
        tabs={(["history", "genealogy", "reservations"] as const).map((key) => ({
          id: key,
          label: t(`inventory.unitCard.tabs.${key}`),
          "data-testid": `unit-tab-${key}`,
        }))}
      />

      <div className="detail-panel" data-testid="unit-panel">
        {tab === "history" ? <HistoryTab unit={unit} /> : null}
        {tab === "genealogy" ? <GenealogyTab unit={unit} canTrace={canTrace} /> : null}
        {tab === "reservations" ? <ReservationsTab unit={unit} /> : null}
      </div>
    </section>
  );
}

const ACTION_LABEL: Readonly<Record<UnitCardAction, string>> = {
  cut: "inventory.unitCard.actionCut",
  remeasure: "inventory.unitCard.actionRemeasure",
  move: "inventory.unitCard.actionMove",
  reserve: "inventory.unitCard.actionReserve",
  quarantine: "inventory.unitCard.actionQuarantine",
  release: "inventory.unitCard.actionRelease",
  retire: "inventory.unitCard.actionRetire",
};

/**
 * The §4.2 action bar. Every action the role holds is drawn; the ones the
 * unit's status rules out are disabled and point at one line that says why
 * (rev-7: a retired unit used to offer all six, and each press was a 422).
 */
function UnitActionBar({
  status,
  reasonId,
  actions,
  onAction,
}: {
  status: string;
  reasonId: string;
  actions: readonly { key: UnitCardAction; testId?: string }[];
  onAction: (key: UnitCardAction) => void;
}) {
  const { t, i18n } = useTranslation();
  if (actions.length === 0) return null;
  const blocks = actions.map((a) => unitActionBlock(status, a.key));
  // One line for the whole bar: the reason that rules out the most actions.
  const reason = mostCommon(blocks);
  return (
    <div className="unit-actions-wrap">
      <div className="unit-actions" role="group" aria-label={t("inventory.unitCard.actions")}>
        {actions.map((a, i) => {
          const blocked = blocks[i] !== null;
          return (
            <Button
              key={a.key}
              data-testid={a.testId ?? `unit-action-${a.key}`}
              disabled={blocked}
              aria-describedby={blocked ? reasonId : undefined}
              onClick={() => {
                onAction(a.key);
              }}
            >
              {t(ACTION_LABEL[a.key])}
            </Button>
          );
        })}
      </div>
      {reason !== null ? (
        <p className="unit-actions-reason" id={reasonId} data-testid="unit-actions-reason">
          {blockedReasonText(t, reason, status, i18n.language)}
        </p>
      ) : null}
    </div>
  );
}

function mostCommon(blocks: readonly (UnitBlockReason | null)[]): UnitBlockReason | null {
  const counts = new Map<UnitBlockReason, number>();
  for (const b of blocks) if (b !== null) counts.set(b, (counts.get(b) ?? 0) + 1);
  let best: UnitBlockReason | null = null;
  let n = 0;
  for (const [k, v] of counts) {
    if (v > n) {
      best = k;
      n = v;
    }
  }
  return best;
}
